import React, { useState, useRef, useEffect } from 'react'
import { Element, Button, Message } from 'react-bulma-components'
import { Form, Formik } from 'formik'
import Avatar from '../../../avatar/avatar'
import { useSimulateTwoPartiesPeriodSummaryQuery } from '../../../../queries/period-summaries'
import FormikObserver from '../../../form/formik-observer'
import { CreateTwoPartiesParams } from '../../../../../../backend/src/services/resources/period-summaries/period-summaries.controller.service.types'
import { CloseWeekFormProps, CounterFormValues } from './types'
import { calculateCounterParams } from './utils'
import CounterFormSection from './counter-form-section'
import { TwoPartiesPeriodSummaryBusinessCreationPayload } from '../../../../../../backend/src/services/resources/period-summaries/two-parties.period-summaries.business.service.types'

/**
 * Form component for closing a payroll week
 * Handles counter configuration and form submission
 */
const CloseWeekForm: React.FC<CloseWeekFormProps> = ({
  weekNumber,
  year,
  worker,
  onSubmit,
  onCancel,
}) => {
  const [countersParams, setCountersParams] = useState<
    TwoPartiesPeriodSummaryBusinessCreationPayload['counters']
  >([])
  const [isFormChanging, setIsFormChanging] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout>()

  // Requête initiale avec les paramètres de base (sans compteurs)
  const initialQuery = useSimulateTwoPartiesPeriodSummaryQuery({
    week: Number(weekNumber),
    year: Number(year),
    worker: worker._id,
    counters: [], // Toujours vide pour la requête initiale
  })

  // Requête de simulation seulement quand le formulaire n'est pas en train de changer
  const simulateQuery = useSimulateTwoPartiesPeriodSummaryQuery(
    {
      week: Number(weekNumber),
      year: Number(year),
      worker: worker._id,
      counters: countersParams,
    },
    {
      enabled: !isFormChanging && (countersParams?.length || 0) > 0, // Seulement si pas en train de changer
    },
  )

  // Utiliser les données de simulation si disponibles, sinon les données initiales
  const counters =
    simulateQuery.data?.data?.employerData?.counters ||
    initialQuery.data?.data?.employerData?.counters ||
    {}

  const accountedDuration =
    simulateQuery.data?.data?.durations.accounted ||
    initialQuery.data?.data?.durations.accounted ||
    0
  const workedDuration =
    simulateQuery.data?.data?.durations.worked.total ||
    initialQuery.data?.data?.durations.worked.total ||
    0

  // Cleanup du timeout au démontage du composant
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  /**
   * Handle form submission
   */
  const handleSubmit = () => {
    // Prepare parameters for API call using the extracted calculation function
    const params: CreateTwoPartiesParams = {
      week: Number(weekNumber),
      year: Number(year),
      worker: worker._id,
      counters: countersParams,
    }
    onSubmit(params)
  }

  /**
   * Handle form values change with debouncing to prevent flickering (maybe a refactor is needed)
   */
  const handleFormChange = (values: CounterFormValues) => {
    setIsFormChanging(true) // Marquer qu'on est en train de changer

    const newParams = calculateCounterParams(values, counters)
    setCountersParams(newParams)

    // Nettoyer le timeout précédent
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Après un délai, permettre la nouvelle requête
    timeoutRef.current = setTimeout(() => {
      setIsFormChanging(false)
    }, 500) // 500ms de délai
  }

  const countersIds = Object.keys(counters)
  const counterCount = countersIds.length
  const countersWithAffectsPeriodSummaryAccounted = countersIds.filter(
    counterId => counters[counterId]?.counter.affectsPeriodSummaryAccounted,
  )
  const accountedCounterCount = countersWithAffectsPeriodSummaryAccounted.length

  return (
    <Element>
      <Message color="info" mb={5}>
        <Message.Body>
          Cloturer la semaine {weekNumber} de {year} pour{' '}
          <Element renderAs="strong">
            <Avatar user={worker} />
          </Element>
          .
          <br />
          Les journées de travail de la semaine ne pourront plus être pointées ou modifiées.
        </Message.Body>
      </Message>
      {counterCount !== 0 && (
        <Element mb={4} textWeight="bold">
          Ajustez les valeurs des compteurs avant de clôturer la semaine :
        </Element>
      )}
      <Formik
        initialValues={{ counters: {} }}
        onSubmit={handleSubmit}
        validate={(values: CounterFormValues) => {
          const errors: any = {}

          const counterInValues: string[] = []
          // Validation des compteurs en mode split
          Object.entries(values.counters).forEach(([counterId, counter]) => {
            counterInValues.push(counterId)
            if (counter && !counter.mode) {
              errors[`counters.${counterId}.mode`] = true
            } else if (counter && counter.mode === 'split') {
              const simulatedCounter = counters[counterId]
              const ruleBasedIncrement = simulatedCounter?.ruleBasedIncrement || 0
              const totalSplit = (counter.splitToCounter || 0) + (counter.splitToWeek || 0)

              // Vérifier que la somme des répartitions est égale à ruleBasedIncrement
              if (Math.abs(totalSplit - ruleBasedIncrement) > 1000) {
                // Tolérance de 1 seconde
                errors[`time-input-hours-counters.${counterId}.splitToCounter`] = true
                errors[`time-input-hours-counters.${counterId}.splitToWeek`] = true
              }
            }
          })

          const allCountersWithAffectsPeriodSummaryAccounted =
            countersWithAffectsPeriodSummaryAccounted.every(cId => counterInValues.includes(cId))

          if (!allCountersWithAffectsPeriodSummaryAccounted) {
            errors.counters = true
          }

          return errors
        }}
      >
        {({ values, isValid, dirty }) => {
          return (
            <Form>
              <Element mb={2}>
                {countersIds.map(counterId => (
                  <Element key={counterId} mb={4} data-test={`counter-form-${counterId}`}>
                    <CounterFormSection
                      counterId={counterId}
                      simulatedData={counters[counterId]}
                      accountedDuration={accountedDuration}
                      workedDuration={workedDuration}
                    />
                  </Element>
                ))}
              </Element>

              <Element display="flex" justifyContent="flex-end">
                <Button color="light" onClick={onCancel} mr={2}>
                  Annuler
                </Button>
                <Button
                  color="success"
                  type="submit"
                  disabled={!isValid || (!dirty && accountedCounterCount !== 0)}
                  data-test="close-week-form-submit"
                >
                  Clôturer la semaine
                </Button>
              </Element>
              <FormikObserver onChange={handleFormChange} values={values} />
            </Form>
          )
        }}
      </Formik>
    </Element>
  )
}

export default CloseWeekForm
